// Right-side detail panel. Slides in on row select; closes with Esc / × button.
import { useEffect, useMemo } from "react";
import { useExplorerStore } from "../store/explorerStore";
import { useHotkey } from "../hooks/useHotkey";
import type { Contact } from "../types/inpact";
import { CertificationChip } from "./CertificationChip";
import { SalesforceDeepLink } from "./SalesforceDeepLink";
import { StatusBadge } from "./StatusBadge";
import { displayCountry, flagFor } from "../lib/countryUtils";
import { salesforceLink } from "../lib/salesforceLink";

interface ContactDetailPanelProps {
  rows: Contact[];
}

export function ContactDetailPanel({ rows }: ContactDetailPanelProps) {
  const { selectedContactId, selectContact } = useExplorerStore();
  const contact = useMemo(
    () => rows.find((c) => c.id === selectedContactId) ?? null,
    [rows, selectedContactId],
  );

  const idx = contact ? rows.findIndex((c) => c.id === contact.id) : -1;
  const total = rows.length;

  const close = () => selectContact(null);
  const prev = () => idx > 0 && selectContact(rows[idx - 1].id);
  const next = () => idx >= 0 && idx < total - 1 && selectContact(rows[idx + 1].id);

  useHotkey("escape", close);
  useHotkey("arrowleft", prev);
  useHotkey("arrowright", next);

  // Move focus to the close button when panel opens.
  useEffect(() => {
    if (contact) {
      const btn = document.querySelector<HTMLButtonElement>(".detail-panel__close");
      btn?.focus();
    }
  }, [contact?.id]);

  if (!contact) return null;

  return (
    <aside className="detail-panel" role="complementary" aria-label="Contact details">
      <div className="detail-panel__header">
        <span className="detail-panel__nav">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label="Previous contact"
            disabled={idx <= 0}
            onClick={prev}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label="Next contact"
            disabled={idx >= total - 1}
            onClick={next}
          >
            ›
          </button>
          <span style={{ marginLeft: 8 }}>
            Contact {idx + 1} of {total}
          </span>
        </span>
        <button
          type="button"
          className="detail-panel__close"
          aria-label="Close panel (Esc)"
          onClick={close}
        >
          ✕
        </button>
      </div>

      <div className="detail-panel__body">
        <Summary contact={contact} />
        <InpactSection contact={contact} />
        <CertificationsSection contact={contact} />
      </div>
    </aside>
  );
}

function Summary({ contact }: { contact: Contact }) {
  return (
    <div className="detail-summary">
      <h2 className="detail-summary__name">
        {contact.firstName} {contact.lastName}
        {contact.id && (
          <SalesforceDeepLink
            sfId={contact.id}
            objectType="Contact"
            stopPropagation={false}
          />
        )}
      </h2>
      <div className="detail-summary__sub">
        {contact.email ?? "—"} · {flagFor(contact.countryOfResidence)} {displayCountry(contact.countryOfResidence)}
      </div>
      <div className="detail-summary__badges">
        <StatusBadge status={contact.associateStatus} inactive={contact.inactive} />
        {contact.countryLead && <span className="bool-badge">Country Lead</span>}
        {contact.leadingTeam && <span className="bool-badge">Leading Team</span>}
      </div>
    </div>
  );
}

function InpactSection({ contact }: { contact: Contact }) {
  return (
    <section className="detail-section" data-open="true">
      <button type="button" className="detail-section__header" aria-expanded="true">
        <span className="detail-section__chevron" aria-hidden="true">›</span>
        INPACT
      </button>
      <div className="detail-section__body">
        <FieldRow label="Contact role" value={contact.pwdStatus} />
        <FieldRow label="Country of Birth" value={displayCountry(contact.countryOfBirth)} />
        <FieldRow label="Education" value={contact.educationLevel} />
        <FieldRow label="Application date" value={contact.dateOfApplication} />
        <FieldRow label="Date of Certification" value={contact.dateOfApproval} />
        <FieldRow
          label="Account"
          value={
            contact.account ? (
              <a
                className="cell-account"
                href={salesforceLink("Account", contact.account.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {contact.account.name}
              </a>
            ) : (
              "—"
            )
          }
        />
      </div>
    </section>
  );
}

function CertificationsSection({ contact }: { contact: Contact }) {
  return (
    <section className="detail-section" data-open="true">
      <button type="button" className="detail-section__header" aria-expanded="true">
        <span className="detail-section__chevron" aria-hidden="true">›</span>
        INPACT Certifications
      </button>
      <div className="detail-section__body">
        {contact.certifications.length === 0 ? (
          <span style={{ color: "var(--color-neutral-400)" }}>No certifications recorded.</span>
        ) : (
          <div className="cert-stack">
            {contact.certifications.map((c, i) => (
              <CertificationChip key={i} cert={c} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  const isEmpty = value == null || value === "" || value === "—";
  return (
    <div className="field-row">
      <span className="field-row__label">{label}</span>
      <span className={`field-row__value${isEmpty ? " field-row__value--empty" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}
